'use client';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import styles from './research-motion.module.css';

/**
 * Scroll-reveal wrapper for the research hub surfaces (GEN-2308 motion pass).
 *
 * Fades its content up (or, with `stagger`, its direct children in sequence) the
 * first time it scrolls into view, then unobserves (single-shot). The hidden
 * state is armed in a layout effect AFTER mount and ONLY when motion is allowed,
 * so:
 *  - server-rendered / no-JS output paints fully visible (SEO + a11y safe),
 *  - `prefers-reduced-motion: reduce` never hides anything (checked directly +
 *    netted by the CSS module's reduced-motion block),
 *  - the IntersectionObserver-missing path also stays visible.
 *
 * Arming in `useLayoutEffect` (before paint) means the first painted frame is
 * already the hidden state, so revealing never flashes content in-then-out.
 */

// useLayoutEffect warns during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface ScrollRevealProps {
  readonly children: React.ReactNode;
  readonly className?: string;
  /** Element to render. Defaults to `div`; use `dl` for the stat rows. */
  readonly as?: 'div' | 'dl' | 'section';
  /** Stagger the direct children instead of revealing the block as one. */
  readonly stagger?: boolean;
  /**
   * IntersectionObserver `rootMargin` string. Default (`'0px 0px -12% 0px'`)
   * fires when the element's top edge crosses 12% above the viewport bottom –
   * early enough that reveal completes before the element is fully in view.
   * Pass a tighter value (e.g. `'-20%'`) for tall sections where the default
   * fires too late on mobile.
   */
  readonly rootMargin?: string;
}

export function ScrollReveal({
  children,
  className,
  as = 'div',
  stagger = false,
  rootMargin = '0px 0px -12% 0px',
}: ScrollRevealProps): React.ReactElement {
  const ref = useRef<HTMLElement | null>(null);
  const assignRef = useCallback((node: HTMLElement | null) => {
    ref.current = node;
  }, []);
  const [armed, setArmed] = useState(false);
  const [inView, setInView] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      // Stay fully visible: never arm the hidden class, never observe.
      return;
    }

    setArmed(true);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  const Tag: React.ElementType = as;
  return (
    <Tag
      ref={assignRef}
      className={clsx(
        className,
        armed && (stagger ? styles.stagger : styles.reveal),
        armed && inView && styles.in,
      )}
    >
      {children}
    </Tag>
  );
}
